import { toast } from "react-toastify";

import { AccessControlProvider } from "@refinedev/core";

import { resources } from "@/config/resources";
import { getRequiredPermissions, hasRequiredPermissions } from "@/utils/required-permissions";

import i18next from "i18next";

/**
 * Format error message for permission denial using translations
 */
const formatPermissionError = (action?: string, resource?: string): string => {
  const resourceName = resource ? resource.charAt(0).toUpperCase() + resource.slice(1) : "Resource";
  const actionName = action ? action.charAt(0).toUpperCase() + action.slice(1) : "Access";

  return i18next.t("errors.permissionDenied.actionFormat", {
    action: actionName,
    resource: resourceName,
  });
};

/**
 * Access control provider that checks the user's featuresv2 permissions against each
 * resource's `meta.requiredPermissions` (VMD-796). Shows toast notifications when access is denied
 *
 * "User is not allowed to perform [action]:[ResourceName]"
 *
 * `getPermissions` is the auth provider's: it fails closed with `[]`, so a gated resource is
 * denied until the user record has loaded.
 */
export const createAccessControlProvider = (getPermissions: () => Promise<string[]>): AccessControlProvider => {
  return {
    can: async ({ resource, action, params }) => {
      try {
        // Find resource configuration
        const resourceConfig = resources.find(r => r.name === resource);

        // If resource doesn't exist or has no feature requirements, allow access
        if (!resourceConfig || getRequiredPermissions(resourceConfig).length === 0) {
          return { can: true };
        }

        const features = await getPermissions();

        // Any-of: one held key is enough
        const hasPermission = hasRequiredPermissions(resourceConfig, key => features.includes(key));

        if (!hasPermission) {
          const errorMessage = formatPermissionError(action, resource);

          // Only show toast for explicit access attempts (not menu visibility checks)
          // CanAccess component passes showToast: true in params
          if (params?.showToast) {
            toast.error(errorMessage, {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              theme: "dark",
            });
          }

          return {
            can: false,
            reason: errorMessage,
          };
        }

        return { can: true };
      } catch (error) {
        // Handle permission lookup errors gracefully: fail closed
        console.error("Access control check failed:", error);
        return { can: false };
      }
    },
  };
};
